import { type FormEvent, useEffect, useRef } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';

import { practiceLocationsOptions } from '@/entities/practice';
import { telehealthSettingsOptions } from '@/entities/practice-member-settings';
import { formatRateForInput, serviceCatalogOptions } from '@/entities/service-catalog';
import { clientServiceCodesOptions, useScheduleSessionOptions } from '@/entities/session';
import { formatLocalDate } from '@/shared/lib/time-utils';
import { useSheetStore } from '@/shared/stores/sheet-store';

import { END_OF_DAY_MINUTES } from '../config/schedule-session.config';
import { buildScheduleSessionInput } from '../lib/build-schedule-session-input';
import { formatTimeOfDay } from '../lib/format-time-of-day';
import { getDefaultServiceCode } from '../lib/get-default-service-code';
import { getNextHalfHour } from '../lib/get-next-half-hour';
import { getShiftedEndTime } from '../lib/get-shifted-end-time';
import { parseTimeOfDay } from '../lib/parse-time-of-day';
import {
    type ScheduleSessionClient,
    type ScheduleSessionFormOutput,
    type ScheduleSessionFormValues,
    scheduleSessionFormSchema,
} from './schedule-session-form.schema';

const EMPTY_DEFAULTS: ScheduleSessionFormValues = {
    client: null,
    date: '',
    startTime: '',
    endTime: '',
    locationType: 'IN_PERSON',
    practiceLocationId: '',
    telehealthLink: '',
    phoneNumber: '',
    serviceCode: '',
    fee: '',
};

type Options = {
    client?: ScheduleSessionClient;
};

export function useScheduleSessionForm({ client }: Options = {}) {
    const queryClient = useQueryClient();
    const serviceCatalogQuery = useQuery(serviceCatalogOptions());
    const practiceLocationsQuery = useQuery(practiceLocationsOptions());
    const telehealthSettingsQuery = useQuery(telehealthSettingsOptions());
    const clientServiceCodesQuery = useQuery(clientServiceCodesOptions(client?.id ?? ''));

    const closeCurrentSheet = useSheetStore((state) => state.closeCurrentSheet);
    const scheduleSession = useMutation(useScheduleSessionOptions());

    const form = useForm<ScheduleSessionFormValues, unknown, ScheduleSessionFormOutput>({
        resolver: zodResolver(scheduleSessionFormSchema),
        defaultValues: EMPTY_DEFAULTS,
    });

    // Read during render: formState is a lazy proxy, so a first read inside a handler is stale.
    const { isSubmitted } = form.formState;

    // Defaults apply once, the moment every read has resolved — a later background refetch of any
    // of these queries must never overwrite what the therapist has already edited.
    const hasAppliedDefaultsRef = useRef(false);

    // The field's onChange commits every keystroke before blur fires, so `form.getValues` already
    // holds the new value by the time this runs — this ref keeps the old one as the baseline.
    const previousStartTimeRef = useRef('');

    // Data presence, not isSuccess: a failed background refetch keeps its data but flips status to error.
    const isReady =
        serviceCatalogQuery.data !== undefined &&
        practiceLocationsQuery.data !== undefined &&
        telehealthSettingsQuery.data !== undefined &&
        (!client || clientServiceCodesQuery.data !== undefined);

    const isError =
        serviceCatalogQuery.isLoadingError ||
        practiceLocationsQuery.isLoadingError ||
        telehealthSettingsQuery.isLoadingError ||
        (!!client && clientServiceCodesQuery.isLoadingError);

    // biome-ignore lint/correctness/useExhaustiveDependencies: apply the defaults exactly once, on the transition to ready
    useEffect(() => {
        if (!isReady || hasAppliedDefaultsRef.current) return;
        hasAppliedDefaultsRef.current = true;

        const catalog = serviceCatalogQuery.data;
        const now = new Date();
        const { minutes: startMinutes, dayOffset } = getNextHalfHour(now);
        previousStartTimeRef.current = formatTimeOfDay(startMinutes);
        const serviceCodes = clientServiceCodesQuery.data?.serviceCodes ?? [];
        const serviceCode = getDefaultServiceCode(catalog, serviceCodes);
        const service = catalog.services.find((candidate) => candidate.code === serviceCode);

        const defaultLocation = practiceLocationsQuery.data.find((location) => location.isDefault);

        const telehealth = telehealthSettingsQuery.data.value;
        const telehealthLink =
            telehealth.linkType === 'SAME_FOR_ALL' ? (telehealth.link ?? '') : '';

        const endMinutes = Math.min(
            startMinutes + (service?.durationMinutes ?? 0),
            END_OF_DAY_MINUTES,
        );

        // Sessions never cross midnight (spec Assumptions), so a start that rolled into tomorrow
        // takes the date with it.
        const defaultDate = new Date(now);
        if (dayOffset) defaultDate.setDate(defaultDate.getDate() + 1);

        form.reset({
            client: client ?? null,
            date: formatLocalDate(defaultDate),
            startTime: formatTimeOfDay(startMinutes),
            endTime: formatTimeOfDay(endMinutes),
            locationType: 'IN_PERSON',
            practiceLocationId: defaultLocation?.id ?? '',
            telehealthLink,
            phoneNumber: '',
            serviceCode,
            fee: service ? formatRateForInput(service.rateCents) : '',
        });
    }, [isReady]);

    // Replaces the fee and the end time with the newly selected service's own, discarding whatever
    // the therapist had edited — the same "pick wins over edit" rule `useSaveServiceForm`'s
    // `selectCode` follows for the description field.
    //
    // Reads the cache directly, at call time, rather than `serviceCatalogQuery.data` — the
    // `onCreated` the Add service sheet calls this through was captured before the service
    // existed, so the closure that reaches here can be older than the catalog it needs.
    const onServiceChange = (code: string) => {
        // Revalidates only after a submit attempt, as the fields' own onChange does.
        form.setValue('serviceCode', code, { shouldValidate: isSubmitted });

        const catalog = queryClient.getQueryData(serviceCatalogOptions().queryKey);
        const service = catalog?.services.find((candidate) => candidate.code === code);
        if (!service) return;

        form.setValue('fee', formatRateForInput(service.rateCents), {
            shouldValidate: isSubmitted,
        });

        const startMinutes = parseTimeOfDay(form.getValues('startTime'));
        if (startMinutes !== null) {
            const endMinutes = Math.min(startMinutes + service.durationMinutes, END_OF_DAY_MINUTES);
            form.setValue('endTime', formatTimeOfDay(endMinutes), { shouldValidate: isSubmitted });
        }
    };

    // Leaves an invalid or empty end untouched, deferring to the schema to refuse it on submit.
    const onStartTimeChange = (newStartTime: string) => {
        const oldStartMinutes = parseTimeOfDay(previousStartTimeRef.current);
        previousStartTimeRef.current = newStartTime;
        form.setValue('startTime', newStartTime, { shouldValidate: isSubmitted });

        const newStartMinutes = parseTimeOfDay(newStartTime);
        const endMinutes = parseTimeOfDay(form.getValues('endTime'));
        if (oldStartMinutes === null || newStartMinutes === null || endMinutes === null) return;

        const shiftedEndMinutes = getShiftedEndTime(oldStartMinutes, newStartMinutes, endMinutes);
        form.setValue('endTime', formatTimeOfDay(shiftedEndMinutes), {
            shouldValidate: isSubmitted,
        });
    };

    // Enter in the start field submits without blurring, which otherwise skips the shift `onBlur`
    // applies to the end time — forced here, before the resolver reads the form, so it still lands.
    const onSubmit = (event?: FormEvent<HTMLFormElement>) => {
        const startMinutes = parseTimeOfDay(form.getValues('startTime'));
        if (startMinutes !== null) {
            const formattedStart = formatTimeOfDay(startMinutes);
            if (formattedStart !== previousStartTimeRef.current) onStartTimeChange(formattedStart);
        }

        return form.handleSubmit((values) => {
            scheduleSession.mutate(buildScheduleSessionInput(values), {
                onSuccess: () => closeCurrentSheet(),
            });
        })(event);
    };

    return {
        form,
        isLoading: !isReady,
        isError,
        isPending: scheduleSession.isPending,
        onServiceChange,
        onStartTimeChange,
        onSubmit,
    };
}
